<script>
    import {onDestroy, onMount} from 'svelte';
    import {t} from '$m7-library/utils/lang.js';

    let {
        label = '',
        options = [],
        value = $bindable(),
        placeholder = t('Select an option...'),
        name = '',
        class: cls = '',
        disabled = false,
        // 'down' (Standard) oder 'up'. Aufwaerts, wenn das Feld am unteren Rand eines Bereichs
        // sitzt: dort hat eine nach unten geoeffnete Liste keinen Platz und wird von der
        // Fensterkante oder einem `overflow-hidden`-Vorfahren abgeschnitten. Gemessen im
        // VisualCMS-Admin am „verschieben nach"-Feld unter der Tabelle (26.08.2026).
        //
        // Default bleibt 'down', damit bestehende Aufrufstellen sich nicht aendern.
        direction = 'down'
    } = $props();

    let isOpen = $state(false);
    let searchTerm = $state('');
    let element = $state(null);

    // Find the label for the currently selected value to display in the input
    const selectedLabel = $derived(options.find(opt => opt.value === value)?.label || '');

    // Filter options based on the search term
    const filteredOptions = $derived(
        searchTerm === '' ? options : options.filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    function selectOption(optionValue) {
        value = optionValue;
        searchTerm = ''; // Reset search term after selection
        isOpen = false;
    }

    function handleInput(event) {
        searchTerm = event.target.value;
        isOpen = true;
        // If the user clears the input, deselect the value
        if (searchTerm === '') {
            value = '';
        }
    }

    // Close dropdown when clicking outside
    function handleClickOutside(event) {
        // Use event.composedPath()[0] to get the actual click target,
        // even when the event crosses a Shadow DOM boundary.
        const clickTarget = event.composedPath && event.composedPath()[0];
        if (element && clickTarget && !element.contains(clickTarget)) {
            isOpen = false;
            searchTerm = ''; // Reset search on close
        }
    }

    onMount(() => {
        window.addEventListener('click', handleClickOutside);
    });

    onDestroy(() => {
        window.removeEventListener('click', handleClickOutside);
    });
</script>

<label class="tw:block tw:text-sm tw:font-medium tw:text-gray-900 tw:relative tw:w-full {cls}" bind:this={element}>
    <span class="">{label}</span>
    <!-- Hidden input to carry the actual value for form submission -->
    <input type="hidden" {name} value={value || ''}>

    <!-- The visible input for searching and displaying the selection -->
    <input name={`shadow_${name}`}
           type="text"
           class="tw:bg-gray-50 tw:border tw:border-gray-300 tw:text-gray-900 tw:text-sm tw:rounded-lg tw:focus:ring-blue-500 tw:focus:border-blue-500 tw:block tw:w-full tw:px-2.5 tw:py-2 {disabled ? 'tw:bg-gray-200 tw:cursor-not-allowed' : ''}"
           autocomplete="off"
           {placeholder}
           value={searchTerm || selectedLabel}
           oninput={handleInput}
           onfocus={() => { if (!disabled) isOpen = true; }}
           {disabled}
    />

    <!-- Dropdown list of options -->
    {#if isOpen && filteredOptions.length > 0}
        <!-- `bottom-full mb-1` statt `mt-1`: die Liste sitzt dann UEBER dem Feld. `bottom-full`
             bezieht sich auf das <label>, die Liste steht also oberhalb von Label UND Eingabe. -->
        <div class="tw:absolute tw:z-10 tw:w-full tw:bg-white tw:rounded-md tw:shadow-lg tw:max-h-60 tw:overflow-y-auto tw:border tw:border-gray-200
                    {direction === 'tw:up' ? 'tw:bottom-full tw:mb-1' : 'tw:mt-1'}">
            <ul>
                {#each filteredOptions as option}
                    <li class="tw:p-0 tw:text-sm tw:text-gray-700 tw:hover:bg-gray-100 tw:cursor-pointer {option.value === value ? 'tw:bg-blue-100' : ''}">
                        <button type="button" class="tw:px-4 tw:py-2 tw:cursor-pointer tw:w-full tw:h-full tw:text-left"
                                onmousedown={() => selectOption(option.value)}>
                            {@html option.label}
                        </button>
                    </li>
                {/each}
            </ul>
        </div>
    {/if}
</label>